import { articleContent, type ContentBlock } from '@/lib/articleContent';

export type XatErrorSeverity = 'info' | 'warning' | 'error' | 'critical';

export type XatErrorCategoryId = 'conta' | 'sistema' | 'grupos' | 'tickets';

export type XatErrorEntry = {
  id: string;
  codesLabel: string;
  title: string;
  resolution: string;
  resolutionSteps: string[];
  contexts: string[];
  severity: XatErrorSeverity;
  relatedIds: string[];
  needsTicket: boolean;
  categoryId: XatErrorCategoryId;
  searchText: string;
};

export type XatErrorCategory = {
  id: XatErrorCategoryId;
  heading: string;
  description: string;
  shortTitle: string;
  shortDescription: string;
  errors: XatErrorEntry[];
};

export type XatErrorCallout = {
  type: 'info' | 'tip' | 'warning' | 'success';
  title: string;
  content: string;
};

const CATEGORY_META: Record<
  XatErrorCategoryId,
  { shortTitle: string; shortDescription: string; headingMatch: string; defaultDescription?: string }
> = {
  conta: {
    headingMatch: 'Erros de gestão de conta',
    shortTitle: 'Conta',
    shortDescription: 'Login, senha, cadastro e recuperação.',
  },
  sistema: {
    headingMatch: 'Erros de sistema e bloqueios',
    shortTitle: 'Sistema',
    shortDescription: 'Conexão, bloqueios e falhas internas.',
  },
  grupos: {
    headingMatch: 'Erros de grupos e short names',
    shortTitle: 'Grupos',
    shortDescription: 'Short Names, grupos e chats.',
    defaultDescription:
      'Aparecem ao comprar, reativar ou gerir grupos e short names. As mensagens costumam incluir «Not available», «Name taken» ou «Not allowed».',
  },
  tickets: {
    headingMatch: 'Erros do sistema de tickets',
    shortTitle: 'Tickets',
    shortDescription: 'Sistema de suporte.',
    defaultDescription:
      'Ocorrem ao tentar abrir ou enviar um ticket em xat.com/ticket, geralmente quando o sistema bloqueia o acesso por segurança.',
  },
};

const HEADING_TO_CATEGORY: Record<string, XatErrorCategoryId> = Object.fromEntries(
  Object.entries(CATEGORY_META).map(([id, meta]) => [meta.headingMatch, id as XatErrorCategoryId])
);

const RELATED_OVERRIDES: Record<string, string[]> = {
  'E4-E50': ['E51', 'E6-E19'],
  'E6-E19': ['E47', 'E48', 'E49'],
  'E7': ['E14-E25', 'E26'],
  'E11-E12-E24-E66': ['E14-E25'],
  'E14-E25': ['E11-E12-E24-E66', 'E30-E64', 'E26'],
  'E17': ['E7', 'E26'],
  'E20': ['E22', 'E23', 'E26'],
  'E22': ['E20', 'E23'],
  'E23': ['E20', 'E22'],
  'E26': ['E28-E55', 'E29', 'E14-E25'],
  'E28-E55': ['E29', 'S25', 'S55'],
  'E29': ['E28-E55', 'E31'],
  'E30-E64': ['E14-E25', 'E11-E12-E24-E66'],
  'E31': ['E29'],
  'E46-E67': ['E28-E55', 'E26'],
  'E51': ['E4-E50', 'E7'],
  'S2-S10': ['E4-E50'],
  'S11': ['E20', 'E22', 'E23'],
  'S12': ['S33', 'S55'],
  'S20-S21': ['S63'],
  'S25': ['E28-E55', 'S55', 'S32', 'S30', 'S31'],
  'S26-S56': ['S25', 'S33'],
  'S29': ['S25'],
  'S32': ['S25', 'S55'],
  'S33': ['S12', 'S55'],
  'S55': ['E28-E55', 'S25', 'S12', 'S32'],
  'NA-1-11': ['NA-7'],
  'NA-2-5-8': ['NA-6', 'NA-13'],
  'NA-6': ['NT-2', 'NA-2-5-8'],
  'NT-1': ['NT-2', 'NOT-ALLOWED'],
  'NT-2': ['NA-6', 'GRUPO-6'],
  'NOT-ALLOWED': ['E48', 'NA-12'],
  'GRUPO-6': ['NA-6', 'NT-2'],
  'T403': ['E28-E55', 'E29'],
};

function parseECodes(codeStr: string): string[] {
  return codeStr
    .split(/\s*\/\s*/)
    .map((c) => c.trim())
    .filter(Boolean);
}

function formatCodesLabel(raw: string, categoryId: XatErrorCategoryId): string {
  const trimmed = raw.trim();
  if (categoryId === 'sistema') {
    if (/system\s*problem/i.test(trimmed)) return trimmed;
    return `System Problem ${trimmed}`;
  }
  return trimmed;
}

function buildErrorId(codeStr: string, categoryId: XatErrorCategoryId): string {
  const trimmed = codeStr.trim();

  if (categoryId === 'conta') {
    const codes = parseECodes(trimmed);
    return codes.join('-');
  }

  if (categoryId === 'sistema') {
    const nums = trimmed.match(/\d+/g);
    if (!nums?.length) return `S${trimmed}`;
    if (nums.length > 1) return nums.map((n) => `S${n}`).join('-');
    return `S${nums[0]}`;
  }

  if (categoryId === 'grupos') {
    if (trimmed.startsWith('Not available')) {
      const nums = Array.from(trimmed.matchAll(/\((\d+)\)/g), (m) => m[1]);
      return nums.length > 1 ? `NA-${nums.join('-')}` : `NA-${nums[0] ?? '0'}`;
    }
    if (trimmed.startsWith('Name taken')) {
      const num = trimmed.match(/\((\d+)\)/)?.[1] ?? '1';
      return `NT-${num}`;
    }
    if (trimmed === 'Not allowed') return 'NOT-ALLOWED';
    if (trimmed.includes('Erro (6)')) return 'GRUPO-6';
    return trimmed.replace(/\s+/g, '-').toLowerCase();
  }

  const num = trimmed.match(/\d+/)?.[0];
  return `T${num ?? trimmed}`;
}

function inferSeverity(meaning: string, resolution: string): XatErrorSeverity {
  const text = `${meaning} ${resolution}`.toLowerCase();
  if (
    /excluíd|held|bloquead|banimento|violação|scam|indefinidamente|todos os recursos suspensos|não são resolvidos/.test(
      text
    )
  ) {
    return 'critical';
  }
  if (/aguarde|manutenção|tente novamente|muitas tentativas|sessão expirada|offline/.test(text)) {
    return 'warning';
  }
  if (
    /marque|complete o recaptcha|escolha|use outro|use pelo menos|digite a mesma|confira o e-mail|atualize o chat/.test(
      text
    ) &&
    !/abra ticket|abrir ticket/.test(text)
  ) {
    return 'info';
  }
  return 'error';
}

function inferContexts(
  categoryId: XatErrorCategoryId,
  meaning: string,
  resolution: string,
  codesLabel: string
): string[] {
  const text = `${meaning} ${resolution} ${codesLabel}`.toLowerCase();
  const contexts: string[] = [];
  const add = (label: string, cond: boolean) => {
    if (cond && !contexts.includes(label)) contexts.push(label);
  };

  if (categoryId === 'conta') {
    add('Login', /login|credenciais|senha|pin|ativar|sessão|verificação de segurança/.test(text));
    add('Cadastro', /registr|nome de usuário|termos|e-mail já|id já|novo id/.test(text));
    add('Recuperação', /lostpass|lost access|recuper/.test(text));
    add('Exclusão de conta', /excluir/.test(text));
    add('Alteração de dados', /trocar|alterar|nome|proteção|localização/.test(text));
    add('Transferências', /transfira|xats|days|powers/.test(text));
  }

  if (categoryId === 'sistema') {
    add('Login', /login|sessão|f5/.test(text));
    add('Transferências', /transfer|xats|days|dx|mint|limite/.test(text));
    add('Trades', /trade|troca|jinx|hug/.test(text));
    add('Compras', /compra|loja|promoção/.test(text));
    add('Grupos', /criar grupo|recaptcha/.test(text));
    add('Mobile', /mobile|pc\/navegador/.test(text));
  }

  if (categoryId === 'grupos') {
    add('Grupos', /grupo|chat|staff|delistado/.test(text));
    add('Short names', /short name|nome reservado|name taken|not allowed/.test(text));
    add('Compra de grupo', /comprado|comprar|reativação/.test(text));
  }

  if (categoryId === 'tickets') {
    add('Tickets', true);
    add('Suporte', /voluntário|help|fórum/.test(text));
  }

  if (contexts.length === 0) {
    contexts.push(CATEGORY_META[categoryId].shortTitle);
  }

  return contexts;
}

function parseResolutionSteps(resolution: string): string[] {
  const bySentence = resolution
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  if (bySentence.length <= 1) return [resolution];

  return bySentence;
}

function inferNeedsTicket(resolution: string): boolean {
  return /abra ticket|abrir ticket|ticket em/i.test(resolution);
}

function buildSearchText(
  id: string,
  codesLabel: string,
  title: string,
  resolution: string,
  contexts: string[],
  categoryId: XatErrorCategoryId
): string {
  const sistemaAliases =
    categoryId === 'sistema' ? ['system problem', 'system problem (', 'erro de sistema'] : [];
  return [id, codesLabel, ...sistemaAliases, title, resolution, ...contexts].join(' ').toLowerCase();
}

function buildRelatedIds(id: string, allIds: Set<string>, codesLabel: string): string[] {
  const manual = RELATED_OVERRIDES[id] ?? [];
  const fromCodes = codesLabel.match(/E\d+|S\d+|\d+/g) ?? [];
  const related = new Set<string>();

  for (const rel of manual) {
    if (rel !== id && allIds.has(rel)) related.add(rel);
  }

  for (const code of fromCodes) {
    const normalized = code.startsWith('E') || code.startsWith('S') ? code : `S${code}`;
    if (normalized !== id && allIds.has(normalized)) related.add(normalized);
  }

  return Array.from(related).slice(0, 4);
}

function parseBlocks(blocks: ContentBlock[]): {
  categories: XatErrorCategory[];
  callouts: XatErrorCallout[];
} {
  const categories: XatErrorCategory[] = [];
  const callouts: XatErrorCallout[] = [];
  let currentCategoryId: XatErrorCategoryId | null = null;
  let currentDescription = '';
  const rawEntries: Omit<XatErrorEntry, 'relatedIds' | 'searchText'>[] = [];

  for (const block of blocks) {
    if (block.type === 'heading' && block.level === 2) {
      const catId = HEADING_TO_CATEGORY[block.text];
      if (catId) {
        currentCategoryId = catId;
        currentDescription = '';
      }
      continue;
    }

    if (block.type === 'paragraph' && currentCategoryId) {
      currentDescription = block.text;
      continue;
    }

    if (block.type === 'table' && currentCategoryId) {
      const meta = CATEGORY_META[currentCategoryId];
      const categoryErrors: XatErrorEntry[] = [];

      for (const row of block.rows) {
        const rawCodesLabel = row[0] ?? '';
        const codesLabel = formatCodesLabel(rawCodesLabel, currentCategoryId);
        const title = row[1] ?? '';
        const resolution = row[2] ?? '';
        const id = buildErrorId(rawCodesLabel, currentCategoryId);
        const contexts = inferContexts(currentCategoryId, title, resolution, codesLabel);

        categoryErrors.push({
          id,
          codesLabel,
          title,
          resolution,
          resolutionSteps: parseResolutionSteps(resolution),
          contexts,
          severity: inferSeverity(title, resolution),
          relatedIds: [],
          needsTicket: inferNeedsTicket(resolution),
          categoryId: currentCategoryId,
          searchText: '',
        });
      }

      categories.push({
        id: currentCategoryId,
        heading: meta.headingMatch,
        description: currentDescription || meta.defaultDescription || '',
        shortTitle: meta.shortTitle,
        shortDescription: meta.shortDescription,
        errors: categoryErrors,
      });

      rawEntries.push(...categoryErrors);
      currentCategoryId = null;
      currentDescription = '';
      continue;
    }

    if (block.type === 'info') {
      callouts.push({ type: 'info', title: block.title, content: block.content });
    } else if (block.type === 'tip') {
      callouts.push({ type: 'tip', title: block.title, content: block.content });
    } else if (block.type === 'warning') {
      callouts.push({ type: 'warning', title: block.title, content: block.content });
    }
  }

  const allIds = new Set(rawEntries.map((e) => e.id));
  for (const category of categories) {
    category.errors = category.errors.map((entry) => ({
      ...entry,
      relatedIds: buildRelatedIds(entry.id, allIds, entry.codesLabel),
      searchText: buildSearchText(
        entry.id,
        entry.codesLabel,
        entry.title,
        entry.resolution,
        entry.contexts,
        entry.categoryId
      ),
    }));
  }

  return { categories, callouts };
}

let cachedData: { categories: XatErrorCategory[]; callouts: XatErrorCallout[] } | null = null;

export function getXatErrorsData(): {
  categories: XatErrorCategory[];
  callouts: XatErrorCallout[];
} {
  if (cachedData) return cachedData;

  const article = articleContent.troubleshooting[4];
  if (!article) {
    cachedData = { categories: [], callouts: [] };
    return cachedData;
  }

  cachedData = parseBlocks(article.blocks);
  return cachedData;
}

export function isXatErrorsGuideArticle(categoryId: string, articleSlug: string): boolean {
  return categoryId === 'troubleshooting' && articleSlug === 'erros-do-xat';
}

export const SEVERITY_LABELS: Record<XatErrorSeverity, string> = {
  info: 'Informação',
  warning: 'Aviso',
  error: 'Erro',
  critical: 'Crítico',
};
